/**
 * The one place the browser talks to the API.
 *
 * Two things it does that a bare `fetch` does not:
 *
 *  - keeps the portal header on every request, so SC-02 scoping is structural
 *    rather than something each screen remembers;
 *  - distinguishes the three refusals, because they mean different things to a
 *    person. 401 is "sign in again", 403 is "you may not", and 404 on a scoped
 *    route is "not offered here" — a capability the portal does not have. A UI
 *    that renders all three as "Error" hides the only useful part.
 */

import { plainMessage } from './plain-error';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly detail?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** The session has gone. Nothing to do but sign in again. */
  get isUnauthenticated() { return this.status === 401; }
  /** RL-05 — signed in, but this action is not granted. */
  get isForbidden() { return this.status === 403; }
  /** Either it does not exist, or this portal does not offer it. */
  get isMissing() { return this.status === 404; }
  /** A rule refused: SA-06, an already-decided application, an unsettled balance. */
  get isConflict() { return this.status === 409; }
  /** Field-level validation from a contracts schema. */
  get fieldErrors(): Array<{ path: string; message: string }> {
    const d = this.detail as { errors?: Array<{ path: string; message: string }> } | undefined;
    return d?.errors ?? [];
  }
}

/** Fired once per 401. `SessionProvider` listens and ends the session. */
export const UNAUTHENTICATED_EVENT = 'miraki:unauthenticated';

let currentPortal: string | null = null;

/** Set once by the portal context; every later request carries it. */
export function setPortal(code: string | null): void {
  currentPortal = code;
}
export function getPortal(): string | null {
  return currentPortal;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Suppress the portal header for routes that are portal-agnostic. */
  noPortal?: boolean;
  signal?: AbortSignal;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  if (currentPortal && !opts.noPortal) headers['x-portal'] = currentPortal;

  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method: opts.method ?? 'GET',
      headers,
      // The access token is an httpOnly cookie. It is never read by JavaScript,
      // which is the point: a cookie the page cannot read cannot be exfiltrated
      // by anything that gets script execution on the page.
      credentials: 'same-origin',
      ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
  } catch (e) {
    // A cancelled request is the caller's own doing; anything else is the network, not a rule.
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new ApiError(0, 'Cannot reach the server. Check your connection and try again.');
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const payload: unknown = text ? safeParse(text) : undefined;

  if (!res.ok) {
    const message = plainMessage(res.status, payload, opts.body);
    // The server renews an expired access token from the refresh cookie on its
    // own, so a 401 here means the session is genuinely over — seven days idle,
    // suspended, or signed out elsewhere. Announce it once; the session provider
    // drops to the sign-in screen rather than every screen showing "Unauthorized".
    // Harmless on the sign-in and first-load paths, where nobody is signed in.
    if (res.status === 401) window.dispatchEvent(new Event(UNAUTHENTICATED_EVENT));
    throw new ApiError(res.status, message, payload);
  }
  return payload as T;
}

function safeParse(text: string): unknown {
  try { return JSON.parse(text); } catch { return text; }
}

/**
 * A file the API serves rather than JSON — an invoice PDF. Same cookie session and
 * portal header as `request`; a refusal still surfaces as an `ApiError`.
 */
async function download(path: string): Promise<{ blob: Blob; fileName: string | null }> {
  const headers: Record<string, string> = {};
  if (currentPortal) headers['x-portal'] = currentPortal;
  const res = await fetch(`/api${path}`, { headers, credentials: 'same-origin' });
  if (!res.ok) {
    const text = await res.text();
    const payload: unknown = text ? safeParse(text) : undefined;
    if (res.status === 401) window.dispatchEvent(new Event(UNAUTHENTICATED_EVENT));
    throw new ApiError(res.status, plainMessage(res.status, payload), payload);
  }
  const named = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '');
  return { blob: await res.blob(), fileName: named?.[1] ?? null };
}

export const api = {
  download,
  get: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'PUT', body }),
  /** Partial update. The catalog routes use PATCH, matching the partial Zod schemas. */
  patch: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'PATCH', body }),
  /** Carries a body: a deletion is a decision, and the reason goes to the audit log. */
  del: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'DELETE', body }),
};
